// packages/console/src/components/panels/LiveProbeGraph.tsx
//
// The live-input probe graph: a rolling, real-time bitrate chart for the live
// transcoder, the streaming analog of the file transcoder's probe graph. Where
// the file graph is a precomputed per-frame envelope over a finite source, a
// live stream has no end, so this accumulates the ingest (raw input) and egress
// (encode output) bitrate as they arrive on the live SSE stream and plots a
// moving time window.
//
// Data source: the live-ingest pipeline publishes a program snapshot every ~2s
// carrying ingestBitsPerSec + egressBitsPerSec (the meterProgram ticker), so this
// samples the stream event, skipping the null figures of a bare state transition.
// Styled to the ProbePass mock (status header, readouts, cyan/blue series).

import { useEffect, useMemo, useRef, useState } from "react";
import uPlot from "uplot";
import { Panel, Dot, Read } from "../Parts";
import { UPlotChart } from "../UPlotChart";
import type { LiveStream } from "../../api/live";
import { mbps } from "../../lib/format";

// WINDOW bounds the retained samples. At the snapshot cadence (~2s) this holds
// several minutes of history in the moving window.
const WINDOW = 180;
const CHART_HEIGHT = 150;

// Series palette from the ProbePass mock: ingest reads cyan, egress blue.
const INGEST = "#22D3EE";
const EGRESS = "#4F86F7";

interface Sample {
  t: number; // seconds since the first sample
  ingest: number; // Mb/s
  egress: number; // Mb/s
}

export function LiveProbeGraph({ stream }: { stream: LiveStream | null }) {
  const [samples, setSamples] = useState<Sample[]>([]);
  const startRef = useRef<number | null>(null);

  const onAir = stream?.state === "on_air";
  const ingest = mbps(stream?.ingestBitsPerSec ?? null) ?? 0;
  const egress = mbps(stream?.egressBitsPerSec ?? null) ?? 0;

  // Append a sample only for a real program snapshot (ingest present). A bare
  // state-transition stream event carries null figures, which would plot as a
  // spurious dip, so it is skipped.
  useEffect(() => {
    if (stream?.ingestBitsPerSec == null) return;
    if (startRef.current === null) startRef.current = Date.now();
    const t = (Date.now() - startRef.current) / 1000;
    setSamples((s) => [...s.slice(-(WINDOW - 1)), { t, ingest, egress }]);
  }, [stream, ingest, egress]);

  const data = useMemo<uPlot.AlignedData>(() => {
    const t = samples.map((s) => s.t);
    return [t, samples.map((s) => s.ingest), samples.map((s) => s.egress)];
  }, [samples]);

  const peakIngest = useMemo(() => samples.reduce((m, s) => (s.ingest > m ? s.ingest : m), 0), [samples]);

  const options = useMemo<Omit<uPlot.Options, "width" | "height">>(
    () => ({
      legend: { show: false },
      cursor: { drag: { x: false, y: false }, focus: { prox: 16 } },
      scales: { x: { time: false }, y: { auto: true } },
      axes: [
        {
          scale: "x",
          stroke: "rgba(255,255,255,0.46)",
          grid: { stroke: "rgba(255,255,255,0.06)", width: 1 },
          ticks: { stroke: "rgba(255,255,255,0.06)", width: 1 },
          font: "10px var(--font-mono, monospace)",
          values: (_u, splits) => splits.map((s) => `${Math.round(s)}s`),
        },
        {
          scale: "y",
          stroke: "rgba(255,255,255,0.46)",
          grid: { stroke: "rgba(255,255,255,0.06)", width: 1 },
          ticks: { stroke: "rgba(255,255,255,0.06)", width: 1 },
          font: "10px var(--font-mono, monospace)",
        },
      ],
      series: [
        {},
        { label: "Ingest", stroke: INGEST, width: 1.75, scale: "y", points: { show: false } },
        { label: "Egress", stroke: EGRESS, width: 1.5, scale: "y", points: { show: false } },
      ],
    }),
    [],
  );

  return (
    <Panel
      title="Live input probe"
      style={{ flex: "none" }}
      bodyStyle={{ overflow: "hidden" }}
      right={
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LegendDot color={INGEST} label="Ingest" />
          <LegendDot color={EGRESS} label="Egress" />
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Status + live readouts (ProbePass stat row). */}
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <Dot tone={onAir ? "onair" : stream?.state === "waiting" ? "warn" : "idle"} pulse={onAir} />
          <Read value={ingest.toFixed(1)} unit="Mb/s" label="Ingest" size={16} />
          <Read value={egress.toFixed(1)} unit="Mb/s" label="Egress" size={16} />
          <Read value={peakIngest.toFixed(1)} unit="Mb/s" label="Peak ingest" size={16} />
        </div>

        {samples.length < 2 ? (
          <div
            style={{
              height: CHART_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              font: "var(--t-body-sm)",
              color: "var(--fg4)",
            }}
          >
            {onAir ? "Collecting ingest samples..." : "Waiting for a live ingest to plot."}
          </div>
        ) : (
          <UPlotChart options={options} data={data} height={CHART_HEIGHT} />
        )}
      </div>
    </Panel>
  );
}

// LegendDot is the small color-swatch + label used in the panel header.
function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 8, height: 2, background: color, display: "inline-block" }} />
      <span
        style={{
          font: "500 9px var(--font-sans)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
          color: "var(--fg3)",
        }}
      >
        {label}
      </span>
    </span>
  );
}
